import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Table as KSNMTable } from 'ksnm-common-ui/lib/Table2/Table';
import { getLocalRowsByCheckBoxStatus } from 'ksnm-common-ui/lib/Table2/utils/getLocalRowsByCheckBoxStatus';
import { FILTER } from 'ksnm-common-ui/lib/Table2/utils/TableRequest/TableRequest.interface';

import { RoutePath } from '@components/AppRouter/constants';
import { getExcelWithAudit } from '@src/utils/getExcelWithAudit';
import { useTableTitleWithCount } from '@src/utils/hooks/useTableTitleWithCount';
import { useUpdateTable } from '@src/utils/hooks/useUpdateTable';
import { objectFieldsToSnakeCase } from '@src/utils/objectFieldsToCamelCase';
import { constantStore } from '@stores/constantStore';
import { reportVersionsStore } from '@stores/dictionaryStores/reportVersion/reportVersionsStore';
import { ksnmTableStore } from '@stores/ksnmTableStore/ksnmTableStore';

import { ActionButton } from './ActionButton';
import { columns } from './columns';

import styles from './Table.module.scss';

const { TABLE_NAMES } = constantStore;

export const Table = observer(() => {
  const { breadcrumbs, selectNode, reportVersions, setChecked } = reportVersionsStore;
  const { push } = useHistory();

  const [key, update, externalControl] = useUpdateTable({ tableName: TABLE_NAMES.DICTIONARY_REPORT_VERSION });

  useEffect(() => {
    update();
  }, [selectNode, update]);

  const hardFilter = useMemo(() => {
    if (selectNode) {
      const out = [
        {
          name: 'report_group_code',
          type: FILTER.EQ,
          values: [selectNode.reportGroupCode ?? ''],
          filterIsInverted: false,
        },
      ];

      if (selectNode.reportTypeCode) {
        out.push({
          name: 'report_type_code',
          type: FILTER.EQ,
          values: [selectNode.reportTypeCode],
          filterIsInverted: false,
        });
      }

      return [out];
    }

    return undefined;
  }, [selectNode]);

  const onRowClick = useCallback(
    (value) => {
      push(`${RoutePath.DICTIONARY_REPORT_VERSION}/${value?.code}`);
    },
    [push],
  );

  const localData = useMemo(() => reportVersions.map((item) => objectFieldsToSnakeCase(item)), [reportVersions]);

  const onCheckBoxChange = useCallback(
    async (tr, sr) => {
      // TODO переделать когда, в таблицном компоненте на беке добавят запрос для получение выбранных строк
      const rows = await getLocalRowsByCheckBoxStatus({
        tableRequest: { ...tr, hardFilter },
        localData: {
          data: localData,
          fieldFormat: {},
        },
        selectRows: sr,
        keyField: 'code',
      });

      setChecked(rows);
    },
    [hardFilter, localData, setChecked],
  );

  const title = useMemo(
    () => (
      <div className={styles.breadcrumbs}>
        {breadcrumbs.map((value) => (
          <div key={value}>{value}</div>
        ))}
      </div>
    ),
    [breadcrumbs],
  );

  const [titleWithCount, onTableChange] = useTableTitleWithCount(title);

  if (!selectNode) return null;

  return (
    <div className={styles.table}>
      <KSNMTable
        externalControl={externalControl}
        tableName={TABLE_NAMES.DICTIONARY_REPORT_VERSION}
        columns={columns}
        tableTitle={titleWithCount}
        resource="v_report_version_tc"
        externalFetch={ksnmTableStore.externalFetch}
        externalFetchXLSX={getExcelWithAudit('Справочник "Версии отчетов"')}
        hardFilter={hardFilter}
        onRowClick={onRowClick}
        onCheckBoxChange={onCheckBoxChange}
        extendTableButtons={<ActionButton update={update} />}
        showAggregateRow={false}
        onTableChange={onTableChange}
        key={key}
        keyField="code"
      />
    </div>
  );
});
